// Lazy, single-flight loader for the Google Maps JS API (Places library).
// The script is injected once and shared across all callers; the key is read
// from the CRA env var REACT_APP_GOOGLE_MAPS_API_KEY.
// Google types aren't installed, so the Maps objects are typed as `any`.

let loadPromise: Promise<any> | null = null;

export const isGoogleMapsConfigured = (): boolean =>
  !!process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Google's official inline bootstrap: defines `google.maps.importLibrary` and
// lazy-loads the modern (non-legacy) library set on first use. This is the
// reliable way to get the new Places API (Place, AutocompleteSuggestion) —
// the legacy AutocompleteService / PlacesService are unavailable to new
// Google Cloud customers.
const installImportLibrary = (key: string): void => {
  const w = window as any;
  const google = (w.google = w.google || {});
  const maps = (google.maps = google.maps || {});
  if (maps.importLibrary) return;

  const requested = new Set<string>();
  let scriptPromise: Promise<void> | null = null;
  const loadScript = (): Promise<void> =>
    scriptPromise ||
    (scriptPromise = new Promise<void>((resolve, reject) => {
      const params = new URLSearchParams();
      params.set("key", key);
      params.set("v", "weekly");
      params.set("libraries", Array.from(requested).join(","));
      // Force English for the whole Maps session (suggestion text + details),
      // regardless of the browser/OS locale. The per-request `language`/
      // `requestedLanguage` options reinforce this, but setting it on the loader
      // is what reliably localizes autocomplete predictions to English.
      params.set("language", "en");
      params.set("callback", "google.maps.__ib__");
      maps.__ib__ = resolve;
      const script = document.createElement("script");
      script.id = "google-maps-places";
      script.async = true;
      script.src =
        "https://maps.googleapis.com/maps/api/js?" + params.toString();
      script.onerror = () => reject(new Error("Failed to load Google Maps"));
      document.head.appendChild(script);
    }));

  // Shim that records requested libraries, loads the script, then defers to the
  // real importLibrary that the loaded script installs over this one.
  maps.importLibrary = (name: string) => {
    requested.add(name);
    return loadScript().then(() => maps.importLibrary(name));
  };
};

export const loadGoogleMapsPlaces = (): Promise<any> => {
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    const w = window as any;
    if (!w.google?.maps?.importLibrary) {
      const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!key) throw new Error("Missing REACT_APP_GOOGLE_MAPS_API_KEY");
      installImportLibrary(key);
    }
    await w.google.maps.importLibrary("places");
    return w.google;
  })();

  // Allow a retry if loading failed (e.g. transient network / key error).
  loadPromise.catch(() => {
    loadPromise = null;
  });

  return loadPromise;
};

export interface ParsedAddress {
  address: string; // street number + route
  city: string;
  stateName: string;
  stateCode: string;
  countryName: string;
  countryCode: string; // ISO2
  zip: string;
  // False when Places returned a street/route-level result with no house number.
  hasStreetNumber: boolean;
}

// Flatten an address-components array into our flat address shape. Handles both
// the new Places API `addressComponents` (longText/shortText) and the legacy
// `address_components` (long_name/short_name) so either source works.
export const parseAddressComponents = (components: any[]): ParsedAddress => {
  const list = components || [];
  const get = (type: string) =>
    list.find((c: any) => (c.types || []).includes(type));
  const longOf = (c: any): string => c?.longText ?? c?.long_name ?? "";
  const shortOf = (c: any): string => c?.shortText ?? c?.short_name ?? "";

  const streetNumber = longOf(get("street_number"));
  const route = longOf(get("route"));
  const city =
    longOf(get("locality")) ||
    longOf(get("postal_town")) ||
    longOf(get("sublocality_level_1")) ||
    longOf(get("administrative_area_level_2"));
  const state = get("administrative_area_level_1");
  const country = get("country");
  const zip = longOf(get("postal_code"));

  return {
    address: [streetNumber, route].filter(Boolean).join(" ").trim(),
    city,
    stateName: longOf(state),
    stateCode: shortOf(state),
    countryName: longOf(country),
    countryCode: shortOf(country),
    zip,
    hasStreetNumber: !!streetNumber,
  };
};
